import { SHADER_INPUT_HANDLE_IDS, SHADER_INPUT_TYPES, type ShaderInputSources, type ShaderInputType } from '@/lib/canvas/shader/media/contract'

/** The parts of a React Flow connection this needs. */
export interface ShaderInputConnection {
  source: string
  targetHandle?: string | null
}

/** The parts of an upstream node (`useNodesData`) this needs. */
export interface ShaderInputSourceNode {
  id: string
  type?: string
  data: Record<string, unknown>
}

type State = Record<string, unknown>
type CreatedItem = { type?: string; content?: unknown }

function nodeState(node: ShaderInputSourceNode): State | undefined {
  const state = node.data?.state
  return state && typeof state === 'object' ? (state as State) : undefined
}

function publicUrl(state: State): string | undefined {
  const url = (state.fileMetadata as { publicUrl?: unknown } | undefined)?.publicUrl
  return typeof url === 'string' && url ? url : undefined
}

function createdOfType(state: State, type: string): string[] {
  const items = Array.isArray(state.createdItems) ? (state.createdItems as CreatedItem[]) : []
  return items.filter((item) => item?.type === type && typeof item.content === 'string').map((item) => item.content as string)
}

function nonEmpty(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined
}

function resolveImage(node: ShaderInputSourceNode, state: State): string | undefined {
  const url = publicUrl(state)
  if (url) return url
  // Shader captures are appended (newest last); other image nodes list their result first.
  const images = createdOfType(state, 'image')
  const item = node.type === 'ShaderNode' ? images.at(-1) : images[0]
  if (item) return item
  const firstImage = (state.images as unknown[] | undefined)?.[0]
  return typeof firstImage === 'string' ? firstImage : undefined
}

function resolveVideo(_node: ShaderInputSourceNode, state: State): string | undefined {
  return publicUrl(state) ?? createdOfType(state, 'video')[0]
}

function resolveAudio(_node: ShaderInputSourceNode, state: State): string | undefined {
  return publicUrl(state) ?? createdOfType(state, 'audio')[0]
}

function resolveText(node: ShaderInputSourceNode, state: State): string | undefined {
  // A Text node's answer is its latest created item (appended last) — its
  // prompt is the question, not the text. A Text file is what the user typed.
  const latest = nonEmpty(createdOfType(state, 'text').at(-1))
  if (node.type === 'TextNode') return latest ?? nonEmpty(state.text)
  return nonEmpty(state.prompt) ?? latest ?? nonEmpty(state.text)
}

function resolveModel(_node: ShaderInputSourceNode, state: State): string | undefined {
  // 3D nodes name their files by format (model_glb, model_fbx, ...); only .glb is readable here.
  const glb = (state.output as { model_glb?: { url?: unknown } } | undefined)?.model_glb?.url
  if (typeof glb === 'string' && glb) return glb
  const url = publicUrl(state)
  if (url && /\.glb(\?|#|$)/i.test(url)) return url
  return createdOfType(state, '3d')[0]
}

const RESOLVERS: Record<ShaderInputType, (node: ShaderInputSourceNode, state: State) => string | undefined> = {
  image: resolveImage,
  video: resolveVideo,
  audio: resolveAudio,
  text: resolveText,
  '3d': resolveModel,
}

/**
 * What each connected input of a Shader node carries, keyed by the target
 * handle the edge lands on. Pure: upstream nodes are passed in, so the node
 * wrapper can feed it from `useNodesData` and tests can feed it plain objects.
 */
export function resolveShaderInputs(
  connections: readonly ShaderInputConnection[],
  sourceNodes: readonly (ShaderInputSourceNode | null | undefined)[],
): ShaderInputSources {
  const inputs: ShaderInputSources = {}
  for (const type of SHADER_INPUT_TYPES) {
    const connection = connections.find((c) => c.targetHandle === SHADER_INPUT_HANDLE_IDS[type])
    if (!connection) continue
    const source = sourceNodes.find((n) => n?.id === connection.source)
    const state = source ? nodeState(source) : undefined
    if (!source || !state) continue
    const value = RESOLVERS[type](source, state)
    if (value !== undefined) inputs[type] = value
  }
  return inputs
}

/** Inputs that have an edge, whether or not the upstream node has produced anything yet. */
export function connectedShaderInputTypes(connections: readonly ShaderInputConnection[]): ShaderInputType[] {
  return SHADER_INPUT_TYPES.filter((type) => connections.some((c) => c.targetHandle === SHADER_INPUT_HANDLE_IDS[type]))
}
